import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';

import { DataSeries } from '@next/core-lib/models';
import { Observable, of, throwError } from 'rxjs';

import { catchError, map } from 'rxjs/operators';

import { RVSSettings } from 'src/app/settings/models/setting.model';

import { Environment } from '../../../environment';

import {
  GetReferenceValueSheetsResponse,
  UpsertRefValSheetResponse,
  ReferenceValueSheet,
  ReferenceValueSheetDataSeriesResponse,
  AnimalType,
} from '../models/reference-value-sheet.model';

@Injectable({
  providedIn: 'root',
})
export class ReferenceValueSheetService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private baseUrl = `${this.env.API_BASE_URL}/service/poultry-feed-advice/secure/referenceValueSheet`;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  public getReferenceValueSheetById(
    id: number,
    rvsSettings: RVSSettings,
  ): Observable<ReferenceValueSheet | HttpErrorResponse> {
    const params = new HttpParams()
      .set('temperatureUnit', String(rvsSettings.temperatureUnit))
      .set('feedUnit', String(rvsSettings.feedUnit))
      .set('waterUnit', String(rvsSettings.waterUnit))
      .set('weightUnit', String(rvsSettings.weightUnit));
    return this.http.get<ReferenceValueSheet>(`${this.baseUrl}/${id}`, { params }).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<ReferenceValueSheet>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  public getReferenceValueSheetDataSeriesById(
    id: number,
  ): Observable<DataSeries<number, number> | HttpErrorResponse> {
    return this.http
      .get<ReferenceValueSheetDataSeriesResponse>(`${this.baseUrl}/dataseries/${id}`)
      .pipe(
        map((response) => response.series),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(undefined);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public getReferenceValueSheets(): Observable<ReferenceValueSheet[] | HttpErrorResponse> {
    return this.http.get<GetReferenceValueSheetsResponse>(this.baseUrl).pipe(
      map((response) => response.referenceValueSheets),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<ReferenceValueSheet[]>[]);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  public getStandardReferenceValueSheets(
    country: string,
    animalType: AnimalType,
  ): Observable<ReferenceValueSheet[] | HttpErrorResponse> {
    return this.http
      .get<GetReferenceValueSheetsResponse>(
        this.baseUrl + '/standard/' + country + '/type/' + animalType.toString(),
      )
      .pipe(
        map((response) => response.referenceValueSheets),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<ReferenceValueSheet[]>[]);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public deleteReferenceValueSheet(id: number) {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  public upsertReferenceValueSheet(
    refValSheet: ReferenceValueSheet,
  ): Observable<UpsertRefValSheetResponse | HttpErrorResponse> {
    return this.http.post<UpsertRefValSheetResponse>(`${this.baseUrl}`, refValSheet, {}).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<UpsertRefValSheetResponse>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }
}
